// CRemoteRound — 瀏覽器端的伺服器牌局代理（實作 IRoundDriver）
// 對應 docs/Server-Table-Design.md
//
// 畫面照舊呼叫 dealStaged／replaceNextFlower／beginPlay／submitAction，並訂閱同一條 event bus；
// 差別是動作送到伺服器，回來的事件逐一「先套狀態、再派發」，與本機 CRound 的時序一致。
// - 發牌與補花在進場前就向伺服器一次取回（prepare），所以 dealStaged／replaceNextFlower 仍是同步
// - 其餘操作排進佇列依序送出，避免兩批事件交錯
// - AI 搶先吃碰胡時，在事件之間補一段「思考」停頓，畫面節奏與本機 AI 相同
//
// === 使用圖片 ===
// 無 — 純連線與狀態同步，不渲染任何畫面

import { PlayerActionType, type TPlayerAction } from '@core/actions';
import type { TRoundEventMap } from '@core/events';
import { removeFromConcealed } from '@core/hand';
import type { IFlowerReplacementResult, IRoundDriver } from '@core/round-driver';
import { RoundPhase, type TRoundPhase, type TRoundState } from '@core/round';
import { isFlower } from '@core/tile';
import type { TSeat } from '@core/types';
import type { IEventBus } from '@event-bus/event-bus';
import type { IRoundSettlement } from './account-service';
import { fromWireState, type IWireBatch, type IWireState } from './round-wire';
import type { IDealResult } from './table-session';

const AI_CLAIM_PAUSE_MS = 700; // AI 吃碰胡他家捨牌前的思考停頓

/** 與伺服器溝通的方式（正式為 HTTP，測試可直接接 CTableSession）。 */
export interface IRoundTransport {
  deal(): Promise<IDealResult>;
  begin(): Promise<IWireBatch>;
  act(seat: TSeat, action: TPlayerAction): Promise<IWireBatch>;
  tenpai(seat: TSeat): Promise<IWireBatch>;
  advance(seat: TSeat, proxy: boolean): Promise<IWireBatch>;
}

export interface IRemoteRoundOptions {
  readonly playerSeat: TSeat;
  readonly initialState: IWireState;
  readonly onError: (error: unknown) => void;
  readonly onSettlement?: (settlement: IRoundSettlement) => void; // 先寫回本機快取，再派發 round.ended
  readonly pauseMs?: number; // 測試設 0
}

export class CRemoteRound implements IRoundDriver {
  private state: TRoundState;
  private dealResult: IDealResult | null = null;
  private flowerQueue: IDealResult['flowers'][number][] = [];
  private queue: Promise<void> = Promise.resolve();
  private failed = false;
  /** 這一手的伺服器結算結果（結束後才有）。 */
  lastSettlement: IRoundSettlement | null = null;
  private readonly pauseMs: number;

  constructor(
    private readonly transport: IRoundTransport,
    private readonly bus: IEventBus<TRoundEventMap>,
    private readonly options: IRemoteRoundOptions
  ) {
    this.state = fromWireState(options.initialState);
    this.pauseMs = options.pauseMs ?? AI_CLAIM_PAUSE_MS;
  }

  /** 進場前先取回發牌與補花結果，dealStaged／replaceNextFlower 才能同步執行。 */
  async prepare(): Promise<void> {
    this.dealResult = await this.transport.deal();
    this.takeSettlement(this.dealResult.settlement);
  }

  getPhase(): TRoundPhase {
    return this.state.phase;
  }

  getState(): Readonly<TRoundState> {
    return this.state;
  }

  hasDeclaredTenpai(seat: TSeat): boolean {
    return this.state.declaredTenpai.has(seat);
  }

  start(): void {
    throw new Error('伺服器牌局只支援階段式開局（dealStaged）');
  }

  dealStaged(): void {
    if (!this.dealResult) throw new Error('尚未取得發牌結果，請先 await prepare()');
    if (this.state.phase !== RoundPhase.Init) return;
    this.flowerQueue = [...this.dealResult.flowers];
    this.applySync(this.dealResult.deal);
  }

  replaceNextFlower(): IFlowerReplacementResult | null {
    const next = this.flowerQueue.shift();
    if (!next) return null;
    this.applySync(next.batch);
    return next.result;
  }

  hasUnreplacedFlowers(): boolean {
    return this.state.hands.some((hand) => hand.concealed.some((tile) => isFlower(tile)));
  }

  beginPlay(): void {
    this.enqueue(() => this.transport.begin());
  }

  /**
   * 本家出牌時畫面會立刻讀手牌（補位動畫、是否詢問聽牌），本機 CRound 是同步移除；
   * 這裡先在本地拿掉那張牌，伺服器回來後再以伺服器狀態為準。
   */
  submitAction(seat: TSeat, action: TPlayerAction): void {
    if (action.type === PlayerActionType.Discard && seat === this.options.playerSeat) {
      const hands = [...this.state.hands] as TRoundState['hands'];
      hands[seat] = removeFromConcealed(hands[seat], action.tile);
      this.state = { ...this.state, hands };
    }
    this.enqueue(() => this.transport.act(seat, action));
  }

  /** 畫面宣告後會立刻讀 hasDeclaredTenpai；本地先記上，伺服器確認後以伺服器狀態為準。 */
  declareTenpai(seat: TSeat): void {
    if (this.state.declaredTenpai.has(seat)) return;
    this.state.declaredTenpai.add(seat);
    this.bus.emit('tenpai.declared', { seat });
    this.enqueue(() => this.transport.tenpai(seat), true);
  }

  /** 請伺服器替 seat 決定一步（AI 出牌回合，或本家代打）。完成後才 resolve。 */
  advance(seat: TSeat, proxy: boolean): Promise<void> {
    return this.enqueue(() => this.transport.advance(seat, proxy));
  }

  /** 等目前排隊中的操作全部套用完（測試用）。 */
  idle(): Promise<void> {
    return this.queue;
  }

  // ---- 內部 ----

  private enqueue(send: () => Promise<IWireBatch>, skipTenpaiEvent = false): Promise<void> {
    const run = async (): Promise<void> => {
      if (this.failed) return;
      try {
        await this.apply(await send(), skipTenpaiEvent);
      } catch (error) {
        this.failed = true;
        this.options.onError(error);
      }
    };
    this.queue = this.queue.then(run);
    return this.queue;
  }

  private takeSettlement(settlement: IRoundSettlement | null | undefined): void {
    if (!settlement) return;
    this.lastSettlement = settlement;
    this.options.onSettlement?.(settlement);
  }

  private applySync(batch: IWireBatch): void {
    for (const event of batch.events) this.emit(event.name, event.payload, event.state);
    this.state = fromWireState(batch.state);
  }

  private async apply(batch: IWireBatch, skipTenpaiEvent: boolean): Promise<void> {
    this.takeSettlement(batch.settlement);
    let previous: string | null = null;
    for (const event of batch.events) {
      if (skipTenpaiEvent && event.name === 'tenpai.declared') continue;
      if (this.pauseMs > 0 && this.isAIClaim(event.name, event.payload, previous))
        await new Promise((resolve) => setTimeout(resolve, this.pauseMs));
      this.emit(event.name, event.payload, event.state);
      previous = event.name;
    }
    this.state = fromWireState(batch.state);
  }

  /** 他家捨牌後緊接著別家吃碰胡：本機 AI 會先思考一下，這裡補上同樣的停頓。 */
  private isAIClaim(name: string, payload: unknown, previous: string | null): boolean {
    if (previous !== 'discard.pending' && previous !== 'kong.added-declared') return false;
    if (name === 'round.ended') return true;
    if (name !== 'player.action' && name !== 'meld.formed') return false;
    return (payload as { seat: TSeat }).seat !== this.options.playerSeat;
  }

  private emit(name: string, payload: unknown, state: IWireState): void {
    this.state = fromWireState(state);
    this.bus.emit(name as keyof TRoundEventMap, payload as TRoundEventMap[keyof TRoundEventMap]);
  }
}

export const isRemoteRound = (round: IRoundDriver): round is CRemoteRound =>
  round instanceof CRemoteRound;
